/**
 * Grass tufts (the stylized look) on the tiles near a centre: the player's tile in the game, a
 * specimen's middle in the Tile Lab. A ring a few steps wide, placed again when the centre or the
 * tiles change; tufts shrink towards the ring's edge, so walking grows them in instead of popping
 * them in. Far away and zoomed out, the painted grass of the tiles carries the look alone.
 */
import { type Kind } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import { stepsFrom } from '../planet/steps';
import { isShown } from '../planet/surface';
import { KIND_HEIGHT, radiusOf, STEP, type PlanetView } from '../planet/view';
import { tuftKind } from './catalog';
import { centerOf } from './geometry';
import { createInstances } from './instances';
import { placeTufts } from './tufts';

export interface Grass {
  /** Tufts on the grass within `reach` steps of tile `center`, `perTile` on each grass tile. */
  show(view: PlanetView, center: number, reach: number, perTile: number): void;
  clear(): void;
}

const _c = new THREE.Vector3();

export function createGrass(parent: THREE.Group): Grass {
  const ins = createInstances(parent, { tuft: tuftKind() });
  let steps = new Int32Array(0);
  const queue: number[] = [];
  return {
    show(view, center, reach, perTile) {
      const { grid } = view;
      ins.begin(radiusOf(grid.subdivisions));
      if (center >= 0 && perTile > 0) {
        if (steps.length !== grid.count) steps = new Int32Array(grid.count);
        stepsFrom(grid, center, reach, steps, queue);
        for (const i of queue) {
          // nothing grows through a machine standing on grass
          if (!isShown(view.shown, i) || view.machines?.has(i)) continue;
          const kind = (view.kind[i] ?? 0) as Kind;
          const away = steps[i] ?? 0;
          // full size up to two steps from the edge, then smaller and smaller
          const grow = Math.min(1, Math.max(0.25, (reach + 0.5 - away) / 2));
          ins.at(-1, (view.elev[i] ?? 0) * STEP + KIND_HEIGHT[kind] - 0.004);
          placeTufts(ins, i, centerOf(view, i, _c), kind, perTile, grow);
        }
      }
      ins.end();
    },
    clear() {
      ins.begin(1);
      ins.end();
    },
  };
}
